/**
 * SafetyDatasheetsTable – renders table for safety datasheets with hierarchical structure
 * 
 * Features:
 * - Hierarchical structure: Document Level -> Section Level -> Entity Level
 * - Search controls, sorting, expansion, download menus
 * - 16 standard SDS sections with proper section names and styling
 * - Advanced search with Document-Level and Section-Level filtering
 * 
 * Props expected:
 * - selectedDocument: document data with pages
 * - documentFilename: filename for title display
 * - expandedRows: expansion state
 * - toggleRow: row expansion handler
 * - anchorEl: menu state
 * - handleMenuOpen, handleMenuClose: menu handlers
 * - downloadFiles: download utility function
 * 
 * Author: Galo Alonso Peralta Espinoza  
 * Date: 04-17-2025
 */

/**
 * ## TODOs:
 * ## - Tweak Advanced Searching logic to be 
 * ##    (1) Document-Level, (2) Document-Level Entities. 2 Dropdowns.
 * ##    (1) Section-Level, (2) Section Name, (3) Section Entities. 3 Dropdowns.
 */

import React, { useState, useMemo } from 'react'

// Material UI
import { Typography, Select, MenuItem, Menu } from '@mui/material'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'

// Material UI Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DownloadIcon from '@mui/icons-material/Download'

// Local CSS
import styles from './SafetyDatasheetsTable.module.css'

// Search level types
const SEARCH_LEVELS = {
  NONE: 'none',
  DOCUMENT: 'document',
  SECTION: 'section'
}

// Document-level search attributes
const DOCUMENT_ATTRIBUTES = [
  { key: 'productName', label: 'Product Name' },
  { key: 'productCAS', label: 'Product Code' },
  { key: 'companyName', label: 'Company Name' },
  { key: 'dateOfIssue', label: 'Date of Issue' }
]

/**
 * Safety Datasheets Table Component
 * Renders table for safety datasheets with hierarchical structure:
 * Document Level -> Section Level -> Entity Level
 */
export default function SafetyDatasheetsTable({
  selectedDocument,
  documentFilename,
  expandedRows,
  toggleRow,
  anchorEl,
  handleMenuOpen,
  handleMenuClose,
  downloadFiles
}) {
  // State for search and sort
  const [searchTerm, setSearchTerm] = useState('')
  const [searchLevel, setSearchLevel] = useState(SEARCH_LEVELS.NONE)
  const [documentAttribute, setDocumentAttribute] = useState('Any')
  const [sectionNumber, setSectionNumber] = useState('Any')
  const [sectionAttribute, setSectionAttribute] = useState('Any')
  const [sortColumn, setSortColumn] = useState('productName')
  const [sortDirection, setSortDirection] = useState('asc')

  // Ensure expandedRows is always a Set for consistent API
  const expandedRowsSet = expandedRows instanceof Set ? expandedRows : new Set(expandedRows)

  // Setting up the document title
  const documentTitle = documentFilename ? documentFilename : 'Safety Datasheets Dataframe'

  // Extract document-level information from the first page
  const getDocumentInfo = () => {
    if (!selectedDocument?.pages || selectedDocument.pages.length === 0) {
      return {
        productName: 'N/A',
        productCAS: 'N/A', 
        companyName: 'N/A',
        dateOfIssue: 'N/A'
      }
    }

    const firstPage = selectedDocument.pages[0]
    const entities = firstPage.entities || {}

    return {
      productName: entities.S1_Product_Name?.[0] || 'N/A',
      productCAS: entities.S1_Product_Code?.[0] || 'N/A',
      companyName: entities.S1_Company_Name?.[0] || 'N/A',
      dateOfIssue: entities.S1_Date_of_Issue?.[0] || 'N/A'
    }
  }

  // Standard SDS section names (16 sections)
  const getSectionsData = () => {
    const sectionNames = [
      'Identification',
      'Hazard Identification',
      'Composition/Information on Ingredients',
      'First Aid Measures',
      'Firefighting Measures',
      'Accidental Release Measures',
      'Handling and Storage',
      'Exposure Controls/Personal Protection',
      'Physical and Chemical Properties',
      'Stability and Reactivity',
      'Toxicological Information',
      'Ecological Information',
      'Disposal Considerations',
      'Transport Information',
      'Regulatory Information',
      'Other Information'
    ]

    const sections = []
    for (let i = 1; i <= 16; i++) {
      const sectionData = extractSectionData(i)
      sections.push({
        sectionNumber: i,
        sectionName: sectionNames[i - 1],
        data: sectionData,
        hasData: sectionData.singleLabels.length > 0 || sectionData.parentLabels.length > 0
      })
    }
    return sections
  }

  // Extract and organize data for a specific section
  const extractSectionData = (sectionNumber) => {
    if (!selectedDocument?.pages) {
      return { singleLabels: [], parentLabels: [] }
    }

    const singleLabels = []
    const parentLabels = []
    const sectionPrefix = `S${sectionNumber}_`

    // Collect all entities from all pages for this section
    selectedDocument.pages.forEach(page => {
      if (!page.entities) return

      Object.keys(page.entities).forEach(entityKey => {
        if (entityKey.startsWith(sectionPrefix)) {
          const entityValue = page.entities[entityKey]
          const labelName = entityKey.replace(sectionPrefix, '')

          // Check if it's a single label (array of strings) or parent label (array of objects)
          if (Array.isArray(entityValue)) {
            if (entityValue.length > 0) {
              if (typeof entityValue[0] === 'string') {
                // Single label
                singleLabels.push({
                  name: labelName,
                  values: entityValue
                })
              } else if (typeof entityValue[0] === 'object') {
                // Parent label with entities
                parentLabels.push({
                  name: labelName,
                  entities: entityValue
                })
              }
            }
          }
        }
      })
    })

    return { singleLabels, parentLabels }
  }

  // Render section content with proper layout
  const renderSectionContent = (sectionData) => {
    const { singleLabels, parentLabels } = sectionData

    return (
      <div className={styles.sectionContent}>
        {/* Single Labels - Two Column Layout */}
        {singleLabels.length > 0 && (
          <div className={styles.singleLabelsContainer}>
            <div className={styles.twoColumnLayout}>
              <div className={styles.leftColumn}>
                {singleLabels.filter((_, index) => index % 2 === 0).map((label, index) => (
                  <div key={index} className={styles.singleLabelItem}>
                    <span className={styles.labelName}>{formatLabelName(label.name)}:</span>
                    <span className={styles.labelValue}>{label.values.join(', ')}</span>
                  </div>
                ))}
              </div>
              <div className={styles.rightColumn}>
                {singleLabels.filter((_, index) => index % 2 === 1).map((label, index) => (
                  <div key={index} className={styles.singleLabelItem}>
                    <span className={styles.labelName}>{formatLabelName(label.name)}:</span>
                    <span className={styles.labelValue}>{label.values.join(', ')}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Parent Labels - Full Width Subtitles with Two Column Entities */}
        {parentLabels.map((parentLabel, parentIndex) => (
          <div key={parentIndex} className={styles.parentLabelContainer}>
            {/* Parent Label Subtitle */}
            <div className={styles.parentLabelTitle}>
              {formatLabelName(parentLabel.name)}
            </div>
            
            {/* Entities in Two Column Layout */}
            <div className={styles.entitiesContainer}>
              <div className={styles.twoColumnLayout}>
                <div className={styles.leftColumn}>
                  {parentLabel.entities.filter((_, index) => index % 2 === 0).map((entity, index) => (
                    <div key={index} className={styles.entityItem}>
                      <div className={styles.entityHeader}>Entity {index * 2 + 1}</div>
                      {renderEntityAttributes(entity)}
                    </div>
                  ))}
                </div>
                <div className={styles.rightColumn}>
                  {parentLabel.entities.filter((_, index) => index % 2 === 1).map((entity, index) => (
                    <div key={index} className={styles.entityItem}>
                      <div className={styles.entityHeader}>Entity {index * 2 + 2}</div>
                      {renderEntityAttributes(entity)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Render individual entity attributes
  const renderEntityAttributes = (entity) => {
    return (
      <div className={styles.entityAttributes}>
        {Object.keys(entity).map((key, index) => (
          <div key={index} className={styles.attributeItem}>
            <span className={styles.attributeName}>{formatLabelName(key)}:</span>
            <span className={styles.attributeValue}>{entity[key]}</span>
          </div>
        ))}
      </div>
    )
  }

  // Format label names for display
  const formatLabelName = (name) => {
    return name
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .replace(/\b\w/g, l => l.toUpperCase())
  }

  //** Rendering Expanded Content for Sections **//
  const renderSectionsDetails = () => {
    const sections = getSectionsData()

    return (
      <div className={styles.sectionsContainer}>
        <table className={styles.sectionsTable}>
          <tbody>
            {sections.map((section) => (
              <React.Fragment key={section.sectionNumber}>
                <tr 
                  className={styles.sectionRow}
                  onClick={(e) => {
                    e.stopPropagation()
                    const sectionId = `section-${section.sectionNumber}`
                    toggleRow(sectionId)
                  }}
                >
                  <td>
                    <div className={styles.sectionRowContent}>
                      <span className={styles.sectionName}>
                        Section {section.sectionNumber} - {section.sectionName}
                        {section.hasData && <span className={styles.dataIndicator}> (Has Data)</span>}
                      </span>
                      <div className={styles.sectionActions}>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation()
                            const sectionId = `section-${section.sectionNumber}`
                            toggleRow(sectionId)
                          }}
                          className={styles.expandIcon}
                          size="small"
                        >
                          <ExpandMoreIcon 
                            style={{
                              transform: expandedRowsSet.has(`section-${section.sectionNumber}`) 
                                ? 'rotate(180deg)' 
                                : 'rotate(0deg)',
                              transition: 'transform 0.2s'
                            }}
                          />
                        </IconButton>
                      </div>
                    </div>
                  </td>
                </tr>
                
                {/* Expanded Section Content */}
                {expandedRowsSet.has(`section-${section.sectionNumber}`) && section.hasData && (
                  <tr className={styles.expandedSectionRow}>
                    <td>
                      {renderSectionContent(section.data)}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  // Get available section attributes for the selected section
  const getSectionAttributes = useMemo(() => {
    if (!selectedDocument?.pages || sectionNumber === 'Any') return []

    const sectionData = extractSectionData(parseInt(sectionNumber))
    const attributes = new Set()

    // Add single labels
    sectionData.singleLabels.forEach(label => {
      attributes.add(label.name)
    })

    // Add parent labels
    sectionData.parentLabels.forEach(label => {
      attributes.add(label.name)
    })

    return Array.from(attributes)
  }, [selectedDocument, sectionNumber])

  // Filtered and sorted document info
  const filteredDocumentInfo = useMemo(() => {
    if (!selectedDocument?.pages || searchLevel === SEARCH_LEVELS.NONE) {
      return getDocumentInfo()
    }

    const documentInfo = getDocumentInfo()
    const term = searchTerm.toLowerCase()

    // Document-level search
    if (searchLevel === SEARCH_LEVELS.DOCUMENT) {
      if (!term) return documentInfo

      if (documentAttribute === 'Any') {
        // Search across all document attributes
        return Object.values(documentInfo).some(value => 
          String(value).toLowerCase().includes(term)
        ) ? documentInfo : null
      } else {
        // Search specific document attribute
        return String(documentInfo[documentAttribute]).toLowerCase().includes(term)
          ? documentInfo
          : null
      }
    }

    // Section-level search
    if (searchLevel === SEARCH_LEVELS.SECTION) {
      if (!term) return documentInfo

      const sections = getSectionsData()
      const hasMatchingSection = sections.some(section => {
        if (sectionNumber !== 'Any' && section.sectionNumber !== parseInt(sectionNumber)) {
          return false
        }

        const { singleLabels, parentLabels } = section.data

        // Search in single labels
        const hasMatchingSingleLabel = singleLabels.some(label => {
          if (sectionAttribute !== 'Any' && label.name !== sectionAttribute) {
            return false
          }
          return label.values.some(value => 
            String(value).toLowerCase().includes(term)
          )
        })

        // Search in parent labels
        const hasMatchingParentLabel = parentLabels.some(label => {
          if (sectionAttribute !== 'Any' && label.name !== sectionAttribute) {
            return false
          }
          return label.entities.some(entity => 
            Object.values(entity).some(value => 
              String(value).toLowerCase().includes(term)
            )
          )
        })

        return hasMatchingSingleLabel || hasMatchingParentLabel
      })

      return hasMatchingSection ? documentInfo : null
    }

    return documentInfo
  }, [
    selectedDocument,
    searchTerm,
    searchLevel,
    documentAttribute,
    sectionNumber,
    sectionAttribute
  ])

  const documentInfo = getDocumentInfo()

  return (
    <div className={styles.tableContainer}>
      {/* Section Title */}
      <Typography variant="h6" className={styles.sectionTitle}>
        {documentTitle}
      </Typography>

      {/* Table Controls */}
      <div className={styles.tableControls}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Search Level Selector */}
          <Select
            value={searchLevel}
            onChange={e => {
              setSearchLevel(e.target.value)
              setSearchTerm('')
              setDocumentAttribute('Any')
              setSectionNumber('Any')
              setSectionAttribute('Any')
            }}
            size="small"
            style={{ height: '2rem', fontSize: '1rem', borderRadius: 5 }}
          >
            <MenuItem value={SEARCH_LEVELS.NONE}>None</MenuItem>
            <MenuItem value={SEARCH_LEVELS.DOCUMENT}>Document-Level</MenuItem>
            <MenuItem value={SEARCH_LEVELS.SECTION}>Section-Level</MenuItem>
          </Select>

          {/* Search Bar */}
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            disabled={searchLevel === SEARCH_LEVELS.NONE}
            style={{ 
              opacity: searchLevel === SEARCH_LEVELS.NONE ? 0.5 : 1,
              cursor: searchLevel === SEARCH_LEVELS.NONE ? 'not-allowed' : 'text'
            }}
          />

          {/* Document-Level Attribute Selector */}
          {searchLevel === SEARCH_LEVELS.DOCUMENT && (
            <Select
              value={documentAttribute}
              onChange={e => setDocumentAttribute(e.target.value)}
              size="small"
              style={{ height: '2rem', fontSize: '1rem', borderRadius: 5 }}
            >
              <MenuItem value="Any">Any</MenuItem>
              {DOCUMENT_ATTRIBUTES.map(attr => (
                <MenuItem key={attr.key} value={attr.key}>
                  {attr.label}
                </MenuItem>
              ))}
            </Select>
          )}

          {/* Section-Level Selectors */}
          {searchLevel === SEARCH_LEVELS.SECTION && (
            <>
              {/* Section Number Selector */}
              <Select
                value={sectionNumber}
                onChange={e => {
                  setSectionNumber(e.target.value)
                  setSectionAttribute('Any')
                }}
                size="small"
                style={{ height: '2rem', fontSize: '1rem', borderRadius: 5 }}
              >
                <MenuItem value="Any">Any Section</MenuItem>
                {getSectionsData().map(section => (
                  <MenuItem key={section.sectionNumber} value={section.sectionNumber}>
                    Section {section.sectionNumber} - {section.sectionName}
                  </MenuItem>
                ))}
              </Select>

              {/* Section Attribute Selector */}
              {sectionNumber !== 'Any' && (
                <Select
                  value={sectionAttribute}
                  onChange={e => setSectionAttribute(e.target.value)}
                  size="small"
                  style={{ height: '2rem', fontSize: '1rem', borderRadius: 5 }}
                >
                  <MenuItem value="Any">Any</MenuItem>
                  {getSectionAttributes.map(attr => (
                    <MenuItem key={attr} value={attr}>
                      {formatLabelName(attr)}
                    </MenuItem>
                  ))}
                </Select>
              )}
            </>
          )}
        </div>
        <div style={{ position: 'relative', textAlign: 'center' }}>
          <Tooltip title="Download" arrow>
            <IconButton 
              className={styles.downloadIcon} 
              onClick={handleMenuOpen}
              disabled={!selectedDocument}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <MenuItem onClick={() => {
              downloadFiles(
                [selectedDocument.id],
                'csv',
                () => documentFilename.replace(/\.pdf$/i,''),
                () => selectedDocument.model || 'safety-datasheets',
                false,
                'full-csv'
              )
              handleMenuClose()
            }}>
              Full CSV
            </MenuItem>
            <MenuItem onClick={() => {
              downloadFiles(
                [selectedDocument.id],
                'csv',
                () => documentFilename.replace(/\.pdf$/i,''),
                () => selectedDocument.model || 'safety-datasheets',
                false,
                'simple-csv'
              )
              handleMenuClose()
            }}>
              Simple CSV
            </MenuItem>
            <MenuItem onClick={() => {
              downloadFiles(
                [selectedDocument.id],
                'json',
                () => documentFilename.replace(/\.pdf$/i,'')
              )
              handleMenuClose()
            }}>
              JSON
            </MenuItem>
          </Menu>
        </div>
      </div>

      {/* Safety Datasheets Dataframe Table */}
      <table className={styles.dataframeTable}>
        {/* Table Header */}
        <thead>
          <tr>
            {/* Column Headers - Safety Datasheets Specific */}
            {[
              { key: 'productName',   label: 'Product Name'  },
              { key: 'productCAS',    label: 'Product Code' },
              { key: 'companyName',   label: 'Company Name' },
              { key: 'dateOfIssue',   label: 'Date of Issue' }
            ].map(col => (
              <th
                key={col.key}
                onClick={() => {
                  if (sortColumn === col.key) {
                    setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'))
                  } else {
                    setSortColumn(col.key)
                    setSortDirection('asc')
                  }
                }}
                style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                {col.label}
                {sortColumn === col.key && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
              </th>
            ))}
            <th style={{ textAlign: 'center' }}>Actions</th>
          </tr>
        </thead>
        {/* Table Body */}
        <tbody>
          {/* Document Level Row */}
          {filteredDocumentInfo && (
            <React.Fragment key="document">
              {/* Main Document Row */}
              <tr onClick={(e) => toggleRow('document', e)} className={styles.documentRow}>
                <td>{filteredDocumentInfo.productName}</td>
                <td>{filteredDocumentInfo.productCAS}</td>
                <td>{filteredDocumentInfo.companyName}</td>
                <td>{filteredDocumentInfo.dateOfIssue}</td>
                <td style={{ textAlign: 'center' }}>
                  <IconButton
                    onClick={(e) => toggleRow('document', e)}
                    className={styles.expandIcon}
                  >
                    {expandedRowsSet.has('document') ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </td>
              </tr>
              {/* Collapsible Sections Logic */}
              {expandedRowsSet.has('document') && (
                <tr className={styles.expandRow}>
                  <td colSpan={5} style={{ padding: 0 }}>
                    {renderSectionsDetails()}
                  </td>
                </tr>
              )}
            </React.Fragment>
          )}
        </tbody>
      </table>
    </div>
  )
} 